import { Box, Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ClientLegelDTO,
  deleteClientLegal,
  getClientLegal,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsDrawer,
  WsIconEdit,
  WsIconMoreVer,
  WsIconText,
  WsListingLoader,
} from 'wisestep-base';

import { ConfirmDeleteDialog } from '../../../../components';
import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext/WorkspaceContext';
import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import PopoverOnClick from '../../../../features/clientListingPage/components/PopoverOnClick';
import { getCommonGridStyles } from '../../../../features/clientListingPage/helper/ClientListingHelper';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import ClientLegalIdentifiers from '../../../clientListingPage/components/ClientLegalIdentifiers';
import IClientLegel from '../../../clientListingPage/types/IClientLegel';
import { legalIdentifierDefaultPayload } from '../../../clientsSettings/helper/ClientSettingsHelper';
import { CustomToolbar } from '../../../invoicing/components/DataGridCustomToolBar';
import { IClientDetailsTabs, clientsAmplitudeEvents } from '../../helper/clientDetailsHelper';
import AddEntityButton from '../addEntityButton/AddEntityButton';

export interface ILegalState {
  openAddOrEditDialog: boolean;
  editFlag: boolean;
  openDeleteDialog: boolean;
}

export const initialState: ILegalState = {
  openAddOrEditDialog: false,
  editFlag: false,
  openDeleteDialog: false,
};

const LegalIdentifiersTab = ({ clientId, clientLegalName }: IClientDetailsTabs) => {
  const [clientLegalIdentifiers, SetClientLegalIdentifiers] = useState<ClientLegelDTO[]>([]);

  const tableData: Record<string, unknown>[] = [];

  const [selectedLegalEntity, setSelectedLegalEntity] = useState<IClientLegel>({
    data: { id: '', legalIdentifierDTO: legalIdentifierDefaultPayload, value: '', clientId: '' },
    isEditable: true,
  });

  const [openDialogs, setOpenDialogs] = useState<ILegalState>(initialState);

  const [loader, setLoader] = useState<boolean>(false);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspace = useWorkspaceContext();

  const isAuthorizedAddLegalIndentifiers = useClientAuthz(ClientAction.ADD_VIEW_LEGAL_IDENTIFIERS);
  const isAuthorizedeEditLegalIndentifiers = useClientAuthz(
    ClientAction.EDIT_VIEW_LEGAL_IDENTIFIERS
  );
  const isAuthorizedDeleteLegalIndentifiers = useClientAuthz(
    ClientAction.DELETE_VIEW_LEGAL_IDENTIFIERS
  );

  const fetchData = useCallback(async () => {
    try {
      setLoader(true);
      const response = await getClientLegal(clientId);
      SetClientLegalIdentifiers(response.data.data);
      setLoader(false);
    } catch {
      setNotification(failed(`Failed to fetch legal identifiers list`));
    }
  }, [clientId, setNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      hide: true,
    },
    {
      field: 'type',
      headerName: 'Legal Identifier Type',
      flex: 1,
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
    },
    {
      field: 'value',
      headerName: 'Legal Identifier Value',
      flex: 1,
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <Box p={2}>
            {isAuthorizedeEditLegalIndentifiers && (
              <WsIconText
                text="Edit"
                textCursor="pointer"
                icon={<WsIconEdit />}
                onClick={() => onEdiLegalIdentifier(params.row)}
              />
            )}

            {/* {isAuthorizedDeleteLegalIndentifiers && (
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                cursor="pointer"
                onClick={() => onDelete(params.row)}
              >
                delete
              </WsTypography>
            )} */}
          </Box>
        </PopoverOnClick>
      ),
    },
  ];

  clientLegalIdentifiers?.map((legalData) => {
    tableData.push({
      name: clientLegalName ?? 'Not Available',
      id: legalData?.legalIdentifierDTO?.id,
      type: legalData?.legalIdentifierDTO.name,
      country: legalData?.legalIdentifierDTO.country,
      value: legalData?.value,
    });
  });

  const handleCloseDialog = () => {
    fetchData();
    setOpenDialogs(initialState);
  };

  const onAddButtonClick = () => {
    clientsAmplitudeEvents(
      'client_details_legal_identfiers_add',
      'Client Details Legal Identifiers Add',
      workspace
    );
    setOpenDialogs({
      ...openDialogs,
      editFlag: false,
      openAddOrEditDialog: true,
    });
  };

  const onEdiLegalIdentifier = (legal: ClientLegelDTO) => {
    clientsAmplitudeEvents(
      'client_details_legal_identfiers_edit',
      'Client Details Legal Identifiers Edit',
      workspace
    );
    setOpenDialogs({ ...openDialogs, editFlag: true, openAddOrEditDialog: true });
    fetchAndSetDataByIndex(legal);
  };

  // const onDelete = (legal: ClientLegelDTO) => {
  //   setOpenDialogs({ ...openDialogs, openDeleteDialog: true });
  //   fetchAndSetDataByIndex(legal);
  // };

  const fetchAndSetDataByIndex = (legal: ClientLegelDTO) => {
    const selected = clientLegalIdentifiers.find(
      (legalEntity) => legalEntity.legalIdentifierDTO.id === legal.id
    );

    if (selected) {
      const legalIdentifierDataObj: IClientLegel = {
        data: {
          ...selected,
          clientId: clientId,
        },
        isEditable: true,
      };
      setSelectedLegalEntity(legalIdentifierDataObj);
    } else
      setNotification(
        failed('Failed to fetch selected legal identifiers details, Please try again !!')
      );
  };
  const deleteClientLegalIdentifier = async () => {
    try {
      if (selectedLegalEntity?.data && selectedLegalEntity?.data.id) {
        await deleteClientLegal(selectedLegalEntity?.data?.id);
        const avPayload = { ...activityLogDTOHelper };
        avPayload.action = LogAction.Delete;
        avPayload.description = `deleted ${selectedLegalEntity.data.value} in Legal Identifier for`;
        avPayload.entityType = LogEntityType.Legal;
        avPayload.entityId = selectedLegalEntity.data.id;
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.parentEntitiyId = selectedLegalEntity.data.clientId;
        avPayload.entityName = selectedLegalEntity.data.legalIdentifierDTO.name;
        avPayload.field = `Legal Identifier`;
        avPayload.contextData = { clientId: selectedLegalEntity.data.clientId };
        await saveActivityLog(avPayload);
        setNotification(success('Legal identifiers has been deleted successfully !!'));
        handleCloseDialog();
      }
    } catch (e) {
      setNotification(failed('Something went wrong, Please try again'));
    }
  };

  return (
    <>
      <Grid item xs={12} mt={1}>
        {clientLegalIdentifiers?.length > 0 && !loader ? (
          <>
            {isAuthorizedAddLegalIndentifiers && (
              <AddEntityButton
                buttonText="Add Legal Identifier"
                onClick={onAddButtonClick}
                justifyContent="flex-end"
              />
            )}

            {isAuthorizedeEditLegalIndentifiers || isAuthorizedDeleteLegalIndentifiers ? (
              <Grid xs={12} sx={{ backgroundColor: 'white' }} mt={1}>
                <DataGrid
                  autoHeight={true}
                  components={{ Toolbar: CustomToolbar }}
                  columns={columns}
                  rows={tableData}
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  paginationMode="server"
                  sortingMode="server"
                  getRowHeight={() => 'auto'}
                  sx={getCommonGridStyles()}
                />
              </Grid>
            ) : (
              ''
            )}
          </>
        ) : loader ? (
          <WsListingLoader />
        ) : (
          <>
            {
              <NoDataFoundCard
                text={
                  isAuthorizedAddLegalIndentifiers
                    ? 'Click on +Add Legal Identifier to add Legal Identifiers'
                    : 'No Legal Identifiers available for the client'
                }
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={
                  isAuthorizedAddLegalIndentifiers ? (
                    <AddEntityButton
                      buttonText="Add Legal Identifier"
                      onClick={onAddButtonClick}
                      justifyContent="center"
                    />
                  ) : (
                    <></>
                  )
                }
              />
            }
          </>
        )}
      </Grid>
      {openDialogs.openAddOrEditDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsDrawer
            open={openDialogs.openAddOrEditDialog}
            onClose={handleCloseDialog}
            title="Add Legal Identifiers"
          >
            <Box m={2}>
              <ClientLegalIdentifiers
                clientId={clientId}
                listPage={true}
                loadTabData={fetchData}
                showModal={() => setOpenDialogs({ ...openDialogs, openAddOrEditDialog: false })}
                editObj={selectedLegalEntity}
                editFlag={openDialogs.editFlag}
              />
            </Box>
          </WsDrawer>
        </ErrorBoundary>
      )}
      {openDialogs.openDeleteDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <ConfirmDeleteDialog
            open={openDialogs.openDeleteDialog}
            onClose={() => setOpenDialogs({ ...openDialogs, openDeleteDialog: false })}
            onConfirm={deleteClientLegalIdentifier}
            deletingItem={`${selectedLegalEntity?.data?.value ?? 'Not Available'} Legal identifier`}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

export default LegalIdentifiersTab;
